import {
  Color,
  ensureContrast,
  filterClass,
  getImageColorFilterId,
  getLightenedColor,
  getShadedColor,
  isCssSafeColorValue,
  referenceModelClassName,
  referenceTypeClassName,
} from '@ardoq/color-helpers';
import {
  CONDITIONAL_IMAGE_COLORING_CLASS_NAME,
  LI_BACKGROUND_CLASS_NAME,
} from '@ardoq/global-consts';
import {
  APIReferenceType,
  ArdoqBlob,
  ArdoqId,
  Entity,
  getEntityType,
} from '@ardoq/api-types';
import { modelOperations } from '@ardoq/core';

export type CssEntry = [selector: string, definition: string];
export const BACKGROUND_CLASS_NAME = 'background';

/**
 * Returns a filter property url value for a given conditional formatting color or an empty string if no color
 * provided.
 */
export const getImageColorFilterValueForColor = (
  color?: ArdoqId | null
): string => (color ? `url(#${getImageColorFilterId(color)})` : '');

/**
 * Prefix used for per-entity CSS keys / class names generated by
 * {@link getEntityCSSKey}. Exported so consumers that classify CSS keys (e.g.
 * the modelCssManager's cascade-ordering bucketing) can do so without
 * duplicating the literal.
 */
export const ENTITY_CSS_KEY_PREFIX = 'id_';

/**
 * Used as an identifier by the CSS Manager to associate styles with entities,
 * as well as a class name. Note that as a class it cannot use our `_id`
 * directly as a selector because it can start with a number, which is not
 * valid in CSS.
 */
const getEntityCSSKey = ({ _id }: Entity): string => {
  return `${ENTITY_CSS_KEY_PREFIX}${_id}`;
};

const isEntityCssKey = (key: string): boolean =>
  key.startsWith(ENTITY_CSS_KEY_PREFIX);

/**
 * Flattens an accumulated CSS-entry Map into the document order required by
 * the CSS cascade.
 *
 * Rules with equal specificity (e.g. `.component.libackground.<typeId>` vs
 * `.component.libackground.id_<componentId>`) are decided by document order —
 * later wins. We group entries into three buckets in increasing precedence:
 *
 *   1. model / workspace-type rules (e.g. `.component.libackground.<typeId>`)
 *   2. per-entity rules (keys matched by {@link isEntityCssKey}) — these are
 *      Edit Style colors set on individual components / references and must
 *      override the type color.
 *   3. conditional formatting filter rules (the `filterStylesKey` entry) —
 *      must override everything else.
 *
 * Without this ordering, the natural Map insertion order can place
 * per-entity rules before model rules when dispatches arrive in the wrong
 * order, and Edit Style colors get masked by the type color.
 */
const orderEntriesByCascade = (
  styles: Map<string, CssEntry[]>,
  filterStylesKey: string
): CssEntry[] => {
  const filterEntries = styles.get(filterStylesKey);
  const modelBucket: CssEntry[][] = [];
  const perEntityBucket: CssEntry[][] = [];
  for (const [key, value] of styles) {
    if (key === filterStylesKey) continue;
    if (isEntityCssKey(key)) {
      perEntityBucket.push(value);
    } else {
      modelBucket.push(value);
    }
  }
  return [
    ...modelBucket,
    ...perEntityBucket,
    ...(filterEntries ? [filterEntries] : []),
  ].flat();
};

const getCSSForEntity = (ardoqEntity: ArdoqBlob & Entity): string => {
  return `${getEntityType(ardoqEntity)} id_${ardoqEntity._id}`;
};

/**
 * Filter will be applied to an image if
 * 1) .CONDITIONAL_IMAGE_COLORING_CLASS_NAME is an ancestor of .component, which is an ancestor of the img
 * 2) img is a descendant of a .component with .CONDITIONAL_IMAGE_COLORING_CLASS_NAME
 *
 * https://ardoqcom.atlassian.net/wiki/spaces/INSIGHT/pages/2532966456/Image+color+filters
 */
const conditionalFormattingImageColoringSelector = (fName: string) =>
  `.${CONDITIONAL_IMAGE_COLORING_CLASS_NAME} .component.${fName} img, .${CONDITIONAL_IMAGE_COLORING_CLASS_NAME}.component.${fName} img`;

function getCSSForComp(key: string, color: Color): CssEntry[] {
  const shadedColor = getShadedColor(color);
  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);
  return [
    [`.component.${key}`, `color:${color}; fill:${color}`],
    [`.component-stroke.${key}`, `stroke: ${shadedColor};`],
    [
      `.component.${BACKGROUND_CLASS_NAME}.${key}`,
      `background-color:${color} !important; fill:${color}`,
    ],
    [
      `.component.${LI_BACKGROUND_CLASS_NAME}.${key}`,
      [
        `background-color: ${lightenedColor} !important;`,
        `fill: ${lightenedColor};`,
        `border-color: ${contrastColor} !important;`,
        `stroke: ${contrastColor} !important`,
      ].join(' '),
    ],
  ];
}

const toComponentTypeStyles = (key: string, color: Color): CssEntry[] => {
  const shadedColor = getShadedColor(color);
  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);

  return [
    [`.component.${key}`, `fill:${color};color:${color};`],
    [`.component-stroke.${key}`, `stroke: ${shadedColor};`],
    [
      `.component.${BACKGROUND_CLASS_NAME}.${key}`,
      `background-color:${color} !important;fill:${color};`,
    ],
    [
      `.component.${LI_BACKGROUND_CLASS_NAME}.${key}`,
      [
        `background-color: ${lightenedColor} !important;`,
        `fill: ${lightenedColor};`,
        `border-color: ${contrastColor} !important;`,
        `stroke: ${contrastColor} !important;`,
      ].join(' '),
    ],
  ];
};

const referenceTypeStyles = (
  modelId: string,
  { id, line, color: modelColor }: APIReferenceType
): [regularStyle: CssEntry, backgroundStyle: CssEntry] => {
  const color =
    modelColor && isCssSafeColorValue(modelColor) ? modelColor : 'black';
  const dasharray = modelOperations.lineTypeToStrokeDasharray(line);
  const lineTypeCss = dasharray ? `stroke-dasharray: ${dasharray};` : '';

  const referenceClassName = `${referenceModelClassName(
    modelId
  )}.${referenceTypeClassName(id)}`;

  const lightenedColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedColor, color);

  const referenceTypeSelector = `.integration.${referenceClassName}`;
  const referenceTypeBackgroundSelector = `.integration.${LI_BACKGROUND_CLASS_NAME}.${referenceClassName}`;
  const referenceTypeCss = `color: ${color}; stroke:${color};${lineTypeCss}`;
  const referenceTypeBackgroundCss = `border-color: ${contrastColor}; background-color: ${lightenedColor};`;
  return [
    [referenceTypeSelector, referenceTypeCss],
    [referenceTypeBackgroundSelector, referenceTypeBackgroundCss],
  ];
};

const toComponentFilterColorStyles = (
  key: string,
  color: Color
): CssEntry[] => {
  const shadedFilterColor = getShadedColor(color);
  const lightenedFilterColor = getLightenedColor(color);
  const contrastColor = ensureContrast(lightenedFilterColor, color);

  return [
    [
      `.component.${key}`,
      [`color: ${color} !important;`, `fill: ${color} !important;`].join(' '),
    ],
    [
      conditionalFormattingImageColoringSelector(key),
      `filter: ${getImageColorFilterValueForColor(color)}`,
    ],
    [`.component.${key} .component-name`, `color:${color} !important;`],

    [`.component-stroke.${key}`, `stroke: ${shadedFilterColor};`],
    [
      `.component.${BACKGROUND_CLASS_NAME}.${key}`,
      `background-color:${color} !important; fill:${color}!important;`,
    ],

    [
      `.component.${LI_BACKGROUND_CLASS_NAME}.${key}`,
      [
        `background-color: ${lightenedFilterColor} !important;`,
        `fill: ${lightenedFilterColor} !important;`,
        `border-color: ${contrastColor} !important;`,
        `stroke: ${contrastColor} !important`,
      ].join(' '),
    ],
    [`.component.group.${key}`, `background-color:${color} !important;`],
  ];
};

const getFilterReferenceColorStyles = (
  key: string,
  color: Color
): CssEntry[] => {
  const lightenedFilterReferenceColor = getLightenedColor(color);
  const borderColor = ensureContrast(lightenedFilterReferenceColor, color);

  return [
    [
      `.integration.${key}`,
      `color:${color} !important;stroke:${color} !important`,
    ],
    [
      `.integration.${LI_BACKGROUND_CLASS_NAME}.${key}`,
      [
        `background-color: ${lightenedFilterReferenceColor} !important;`,
        `fill: ${lightenedFilterReferenceColor} !important;`,
        `border-color: ${borderColor} !important;`,
      ].join(' '),
    ],
  ];
};

export type FormattingColors = {
  componentColors: Color[];
  referenceColors: Color[];
};

function toFormattingStyles({
  componentColors,
  referenceColors,
}: FormattingColors): CssEntry[] {
  const componentStyles = componentColors.flatMap(color => {
    const key = filterClass(color) || '';
    return toComponentFilterColorStyles(key, color);
  });

  const referenceStyles = referenceColors.flatMap(color => {
    const key = filterClass(color) || '';
    return getFilterReferenceColorStyles(key, color);
  });

  return componentStyles.concat(referenceStyles);
}

export const entityCssOperations = {
  getCSSForComp,
  getEntityCSSKey,
  isEntityCssKey,
  orderEntriesByCascade,
  getCSSForEntity,
  toComponentTypeStyles,
  toFormattingStyles,
  referenceTypeStyles,
  toComponentFilterColorStyles,
  getFilterReferenceColorStyles,
};
